<template>
  <div>
    <app-button @click.native="onOpenSendEmailPopup('reservation')">
      Send email
    </app-button>
    <app-cells class="cells--end-vertical">
      <app-form-group label="Communication email" class="form-group--no-indent">
        <template #additional>
          <img
            src="@/assets/img/warning-icon.svg"
            alt="Confirmed"
            v-tooltip="{ content: 'If the communication email is not chosen, we will send emails to all flight addresses', placement: 'top', offset: 10 }"
          >
        </template>
        <app-select
          v-model="reservation.communication_email"
          :reduce="item => item.id"
          :options="reservation?.flight?.emails_reservation"
          label="email"
          placeholder="Choose email"
        />
      </app-form-group>
      <app-form-group label="Email subject" label-for="email_subject" class="form-group--no-indent">
        <app-input
          v-model="reservation.email_subject"
          id="email_subject"
          placeholder="Email subject"
          name="email_subject"
          class="input--min-300"
        />
      </app-form-group>
      <app-button
        @click.native="onReservationPatch"
        ref="save_other_options"
        style="align-self: flex-end;"
      >
        Save
      </app-button>
    </app-cells>
    <div v-for="(email, index) in reservations_emails" :key="email.id">
      <app-email-list-multiple-recipient :data="email" :count="index + 1" />
    </div>
    <modal
      name="send-email-template-popup"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('send-email-template-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Send {{ current_action }} email</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckSendEmailForm">
            <app-form-group label="Communication email">
              <template #additional>
                <img
                  v-if="reservation?.flight?.emails_reservation.length > 1"
                  src="@/assets/img/warning-icon.svg"
                  alt="Confirmed"
                  v-tooltip="{ content: 'If the communication email is not chosen, we will send emails to all flight addresses', placement: 'top', offset: 10 }"
                >
              </template>
              <app-select
                v-model="form_send_email.communication_email"
                :reduce="item => item.id"
                :options="reservation?.flight?.emails_reservation"
                label="email"
                placeholder="Choose email"
              />
            </app-form-group>
            <app-form-group label="Template" required>
              <app-select
                v-model="form_send_email.email_template"
                :options="email_templates"
                :clearable="false"
                :get-option-label="item => item.sendpulse_template.name"
                :display-field="option => option.sendpulse_template.name"
                placeholder="Choose template"
                :error="$v.form_send_email.email_template.$error"
              />
              <template #error>
                <div v-if="$v.form_send_email.email_template.$dirty && !$v.form_send_email.email_template.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="send_email_submit" :disabled="$v.form_send_email.$error">
                Send
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AppEmailListMultipleRecipient from '@/components/AppEmailListMultipleRecipient.vue'
import {
  getReservationsEmails,
  getReservationsEmailTemplate,
  patchReservationsFlights,
  postReservationsFlightsEmail
} from '@/http'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FlightsReservationsEmails',
  props: {
    reservation: {
      type: Object,
    },
  },
  components: { AppEmailListMultipleRecipient },
  data() {
    return {
      reservations_emails: [],
      form_send_email: { communication_email: null },
      email_templates: [],
      current_action: '',
    }
  },
  validations: {
    form_send_email: {
      email_template: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_reservations_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_assistant')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
  },
  created() {
    this.getReservationsEmailsList()
  },
  methods: {
    onCheckSendEmailForm() {
      this.$v.form_send_email.$touch()
      if (this.$v.form_send_email.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendEmailForm()
      }
    },
    sendEmailForm() {
      this.$refs.send_email_submit.preload = true
      postReservationsFlightsEmail(this.$route.params.id, this.normalizeEmailForm())
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Reservation email sent',
          })
          this.getReservationsEmailsList()
          this.$modal.hide('send-email-template-popup')
        })
    },
    normalizeEmailForm() {
      const normalizedForm = { ...this.form_send_email }

      normalizedForm.email_template = normalizedForm.email_template.id
      return normalizedForm
    },
    onOpenSendEmailPopup(type) {
      this.current_action = type
      getReservationsEmailTemplate({
        category: type,
        type: 'flight',
        reservation_sales_statuses: this.reservation.sales_status.code,
        reservation_statuses: this.reservation.reservation_status.code,
      })
        .then(res => {
          this.email_templates = res.data
          if (this.email_templates.length === 1) {
            this.form_send_email.email_template = this.email_templates[0]
          }
        })
      if (this.reservation.communication_email) this.form_send_email.communication_email = this.reservation.communication_email
      this.$modal.show('send-email-template-popup')
    },
    onReservationPatch() {
      this.$refs.save_other_options.preload = true
      patchReservationsFlights(
        this.$route.params.id,
        {
          communication_email: this.reservation.communication_email ? this.reservation.communication_email : null,
          email_subject: this.reservation.email_subject || null,
        }
      )
        .finally(() => { this.$refs.save_other_options.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
        })
    },
    getReservationsEmailsList() {
      getReservationsEmails({ flight_reservation_id: this.$route.params.id })
        .then(res => {
          this.reservations_emails = res.data
        })
    },
  }
}
</script>
